import classNames from "classnames";
import { useCallback, useMemo } from "react";
import { useAscContext } from "../../context/AscProvider/AscContext";
import AscButton from "../AscButton/AscButton";
import styles from "./AscLink.module.scss";
import { AscLinkProps } from "./AscLink.type";

/**
 * Adds additional functionality and styling to the linkComponentWrapper.
 * Uses the linkComponentWrapper from the AscContext to wrap the children in a link.
 * linkComponentWrapper is set to a Next.js Link component by default. (via AscProvider)
 * @see AscProvider for linkComponentWrapper
 */
export default function AscLink({
  className,
  children,
  tabIndex,
  icon,
  showIcon = false,
  disabled = false,
  innerWrapperClassName: innerButtonClassName,
  innerWrapperTag: InnerContainerTag = "span",
  ...rest
}: AscLinkProps) {
  //TODO: add support for other custom linkComponentWrapper passed via props
  const { linkComponentWrapper } = useAscContext();

  const innerContent = useMemo(() => {
    switch (InnerContainerTag) {
      case "button":
        return (
          <AscButton
            variant="link"
            hideLinkIcon={!showIcon && !icon}
            type="button"
            icon={icon}
            className={classNames(styles.button, innerButtonClassName)}
          >
            {children}
          </AscButton>
        );
      case null:
        return children;
      default:
        return (
          <InnerContainerTag className={classNames(innerButtonClassName)}>
            {children}
          </InnerContainerTag>
        );
    }
  }, [InnerContainerTag, children, icon, innerButtonClassName, showIcon]);

  const content = useCallback(
    () =>
      linkComponentWrapper({
        className: classNames(
          styles.ground,
          {
            [styles.disabled]: disabled,
          },
          className
        ),
        tabIndex: disabled ? -1 : tabIndex,
        children: innerContent,
        ...rest,
      }),
    [linkComponentWrapper, disabled, className, tabIndex, rest, innerContent]
  );

  return <>{content()}</>;
}
